// Mirrored in frontend/constants/utils/chat_work_capabilities.js. Both deploy
// independently; the parity test prevents drift. Never use a router vote,
// assistant refusal, URL keywords, or attachment count as delegation evidence.

function requestText(message) {
    return (typeof message === 'string' ? message : '')
        .replace(/```[\s\S]*?(?:```|$)|~~~[\s\S]*?(?:~~~|$)/g, '<code>')
        .replace(/^\s*>.*$/gm, ' ')
        .replace(/\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/gi, '$2')
        .replace(/(?:https?:\/\/|www\.)[^\s<>"'`]+/gi, '<link>')
        .replace(/`[^`]*`/g, '<code>')
        .replace(/"[^"\n]*"|“[^”\n]*”|「[^」]*」|«[^»]*»/g, value => value.includes('<link>') ? '<link>' : '<quoted>')
        .replace(/[’‘]/g, "'").normalize('NFKC').replace(/^[¿¡]/, '').trim();
}

// Translate only anchored request grammar, never URLs, quoted text or code.
// Unknown wording stays unknown. Each supported language has negative pairs in
// the shared fixtures; this is not a model vote or an automatic execution path.
const LOCALIZED_REQUESTS = [
    [/^(?:por favor[, ]*)?(?:puedes? |podr[ií]as? )?(?:descarga|descargar|baja)\b/i, 'download'],
    [/^(?:por favor[, ]*)?(?:ejecuta|ejecutar|corre)\b/i, 'run'],
    [/^(?:por favor[, ]*)?(?:convierte|convertir)\b/i, 'convert'],
    [/^(?:por favor[, ]*)?(?:combina|fusiona|une)\b/i, 'merge'],
    [/^(?:s'il te pla[iî]t[, ]*)?(?:(?:peux|pouvez|pourrais|pourriez)[- ](?:tu|vous) )?t[eé]l[eé]charge(?:r|z)?\b/i, 'download'],
    [/^(?:ex[eé]cute[rz]?|lance[rz]?)\b/i, 'run'],
    [/^(?:convertis|convertir|convertissez)\b/i, 'convert'],
    [/^(?:fusionne[rz]?|regroupe[rz]?)\b/i, 'merge'],
    [/^(?:bitte )?(?:(?:kannst|könntest) du )?lade\b/i, 'download'],
    [/^(?:bitte )?f[uü]hre\b/i, 'run'],
    [/^(?:bitte )?konvertiere\b/i, 'convert'],
    [/^(?:por favor[, ]*)?(?:(?:voc[eê] )?pode(?:ria)? )?(?:baixe|baixar)\b/i, 'download'],
    [/^(?:por favor[, ]*)?(?:execute|executar|rode)\b/i, 'run'],
    [/^(?:por favor[, ]*)?(?:converta|converter)\b/i, 'convert'],
    [/^(?:por favor[, ]*)?(?:junte|mescle|unir)\b/i, 'merge'],
    [/^(?:per favore[, ]*)?(?:puoi )?scarica(?:re)?\b/i, 'download'],
    [/^(?:per favore[, ]*)?esegui\b/i, 'run'],
    [/^(?:per favore[, ]*)?converti\b/i, 'convert'],
    [/^(?:per favore[, ]*)?unisci\b/i, 'merge'],
];
function normalizeRequestLanguage(text) {
    for (const [pattern, verb] of LOCALIZED_REQUESTS) {
        if (!pattern.test(text)) continue;
        // Conditional capability questions and instructions ABOUT an action
        // must not become imperatives during translation.
        if (/\b(?:si|se|wenn|nicht|pas)\b|\b(?:comment|cómo|como|wie)\b/i.test(text)) return text;
        return text.replace(pattern, verb)
            .replace(/\b(?:este|esta|estos|estas|estes|esse|essa|ese|esa|ce|cet|cette|ces|diese[snmr]?|dieses|quest[oaie])\b/gi, 'this')
            .replace(/\b(?:el|la|los|las|le|les|mon|mes|meu|minha|il|lo|gli)\b/gi, 'the')
            .replace(/\b(?:v[ií]deos?|vid[eé]os?)\b/gi, 'video')
            .replace(/\b(?:archivos?|fichiers?|dateien?|arquivos?|files?)\b/gi, 'file')
            .replace(/\b(?:pruebas?|testes|tests|test)\b/gi, 'tests')
            .replace(/\b(?:c[oó]digo|code|codice)\b/gi, 'code')
            .replace(/\b(python|javascript|shell|bash)[-‐‑](?:skript|script)\b/gi, '$1 script')
            .replace(/\b(?:scripts?|skript)\b/gi, 'script');
    }
    // CJK grammar is intentionally narrow: a concrete target and a polite or
    // imperative ending, without a question, explanation, quote, or negation.
    if (/[?？]|(?:不要|别|如何|怎么|解释|翻译|说明|できますか|方法|説明|翻訳|しない|하지|방법|설명|번역)/u.test(text)) return text;
    if (/^(?:请|請)?下载(?:这个|這個|此)/u.test(text)) return text.replace(/^(?:请|請)?下载(?:这个|這個|此)/u, 'download this ').replace(/视频|影片/u, 'video');
    if (/^(?:请|請)?(?:运行|执行)(?:这个|此)/u.test(text)) return text.replace(/^(?:请|請)?(?:运行|执行)(?:这个|此)/u, 'run this ').replace(/脚本|代码|测试/u, 'script');
    if (/^この.+(?:ダウンロード|実行)して(?:ください)?/u.test(text)) return `${/ダウンロード/u.test(text) ? 'download this video' : 'run this script'} ${text.includes('<link>') ? '<link>' : ''}`;
    if (/^이 .+(?:다운로드|실행)해\s*주세요/u.test(text)) return `${/다운로드/u.test(text) ? 'download this video' : 'run this script'} ${text.includes('<link>') ? '<link>' : ''}`;
    return text;
}

function declinesWork(message) {
    const text = requestText(message);
    if (/^(?:translate|explain|describe|proofread|rewrite|quote|say|repeat|what|why|how)\b/i.test(stripRequestPrefix(text))) return false;
    return /\b(?:stay|keep (?:this|it|me)) in (?:normal )?chat(?: mode)?\b|\b(?:don't|do not|never)\s+(?:use|suggest|recommend|switch|redirect|move|send|upgrade)\b.{0,45}\bwork(?: mode)?\b|\b(?:stop|quit)\s+(?:suggesting|recommending|offering|pushing|mentioning)\b.{0,30}\bwork\b|\b(?:no|without) work mode\b/i.test(text)
        || /\b(?:no (?:sugieras|recomiendes)|ne (?:suggère|recommande).*pas|não (?:sugira|recomende)|non (?:suggerire|consigliare)).{0,35}\bwork\b/i.test(text)
        || /(?:不要|别).{0,10}(?:推荐|建议).*work/i.test(text);
}

// A one-turn "stay in Chat" remains one-turn. Requests to stop recommendations
// persist within this conversation, including after its messages are archived.
function workSuggestionPreference(message) {
    const text = requestText(message);
    if (/\b(?:this (?:turn|time|request)|for now)\b/i.test(text)) return null;
    if (declinesWork(message) && /\b(?:suggest|recommend|suggesting|recommending|offering|mentioning|pushing|again|ever|anymore|from now on)\b|sugieras|recomiendes|sugira|recomende|suggerire|consigliare|推荐|建议/i.test(text)) return false;
    if (/^(?:please\s+)?(?:you can |you may )?(?:suggest|recommend|offer|show)\s+(?:me\s+)?(?:gab ai )?work(?: mode| suggestions| recommendations)?\s+(?:again|from now on)\b/i.test(text)
        || /^(?:please\s+)?(?:turn|enable)\s+(?:on\s+)?work(?: mode)? suggestions(?: back)?(?: on)?[.!]?$/i.test(text)) return true;
    return null;
}

function workSuggestionsDeclined(message, previousMessages = [], storedDeclined = false) {
    let declined = storedDeclined === true;
    for (const prior of previousMessages) {
        if (prior?.role !== 'user' || prior.flags?.isHidden || prior.flags?.isDeleted) continue;
        const preference = workSuggestionPreference(prior.content);
        if (preference !== null) declined = !preference;
    }
    const preference = workSuggestionPreference(message);
    if (preference !== null) declined = !preference;
    return declinesWork(message) || declined;
}

const WRAPPER = /^(?:explain|describe|teach|summari[sz]e|translate|paraphrase|proofread|rewrite|format|quote|say|repeat|show me|walk me through|give me (?:a |an )?prompt|write (?:a |an )?(?:prompt|story|poem)|(?:how|what|why|when|where|should|can i|could i|do you|are you))\b/i;
function stripRequestPrefix(text) {
    return text.replace(/^(?:hey|hi|hello)[,!]?\s+/i, '')
        .replace(/^(?:please\s+)?(?:(?:can|could|would|will) you\s+|(?:i|we) (?:need|want|would like) you to\s+)?(?:please\s+)?(?:help me\s+)?/i, '');
}

const LOCALIZED_WRAPPER = /^(?:traduce|traduire|traduis|traduza|traduci|übersetze|erkläre|explica|explique|spiega|comment|cómo|como|wie)\b/i;
function actionClauses(message) {
    const text = requestText(message);
    if (declinesWork(message)) return [];
    const clauses = [];
    let payload = false;
    for (let sentence of text.split(/\n+|[.!?;]\s+/)) {
        sentence = sentence.trim();
        const independent = /^(?:separately[, :]|now please\b|but please\b|instead[, ]|then\b)/i.test(sentence);
        if (independent && (!payload || /^(?:separately[, :]|now please\b)/i.test(sentence))) payload = false;
        sentence = sentence.replace(/^(?:separately[, :]*|now |then |but |instead[, ]+)/i, '').trim();
        if (payload) continue;
        let normalized = stripRequestPrefix(sentence);
        if (LOCALIZED_WRAPPER.test(normalized)) { payload = true; continue; }
        if (WRAPPER.test(normalized)) {
            // Preserve ownership of an unquoted pasted payload across lines.
            payload = /^(?:translate|paraphrase|proofread|rewrite|format|quote|say|repeat|show me|walk me through|give me (?:a |an )?prompt|write (?:a |an )?(?:prompt|story|poem))\b/i.test(normalized)
                || /(?:following|this (?:text|command|instruction|example)|how to|:\s*$)/i.test(normalized);
            continue;
        }
        if (/^(?:please\s+)?(?:don't|do not|never|no need to)\b/i.test(sentence)) continue;
        if (/^(?:hey[, ]+)?(?:can|could|would|will) you\b/i.test(sentence)
            && /\b(?:if|when) (?:i|we|you)\b|\blater\b/i.test(sentence)) continue;
        normalized = normalizeRequestLanguage(normalized);
        normalized = normalized.replace(/^(?:i|we) (?:need|want|would like)\s+(.+?)\s+(downloaded|converted|executed|merged|zipped|renamed)(?=\s|:|$)/i,
            (_, target, verb) => `${({ downloaded: 'download', converted: 'convert', executed: 'run', merged: 'merge', zipped: 'zip', renamed: 'rename' })[verb.toLowerCase()]} ${target}`);
        for (const part of normalized.split(/\b(?:and then|then|instead|but)\s+/i)) {
            const clause = stripRequestPrefix(part.trim().replace(/^(?:<link>\s*[:,-]?\s*)+/, ''));
            if (!clause || WRAPPER.test(clause) || /^(?:don't|do not|never|no need to|avoid|without|if|for example|example|imagine|suppose|i |we |he |she |they |you said)\b/i.test(clause)) continue;
            clauses.push(clause);
        }
    }
    return clauses;
}

function getWorkCapabilityGap(message) {
    const text = requestText(message);
    const linked = text.includes('<link>');
    for (const clause of actionClauses(message)) {
        // Only suppress the clause about Chat's delivered output, not an
        // unrelated delegated action in the same message.
        if (/\b(?:file|pdf|document|image)\b.{0,25}\byou (?:just )?(?:made|created|generated|attached)\b|\b(?:last|previous) (?:answer|reply|message)\b/i.test(clause)) continue;
        // A demonstrative or source is required: "can you download videos?"
        // is a capability question, whereas "download this video" delegates.
        const target = /<link>|\b(?:this|that|these|those|the|my|our|attached|uploaded)\b/i.test(clause);
        const mediaTarget = target && /\b(?:video|clip|audio|file|images?|playlist|podcast)\b/i.test(clause);
        if (/^download\b/i.test(clause) && (linked || mediaTarget)) return 'retrieval';
        if (linked && /^(?:give|send|provide)\b.{0,25}\b(?:download (?:link|file)|downloadable (?:copy|file|video))\b/i.test(clause)) return 'retrieval';
        if (/^(?:fetch|grab)\b/i.test(clause)
            && (mediaTarget || /^(?:fetch|grab)\s+(?:(?:me|this|that|it)\s+)*<link>/i.test(clause))) return 'retrieval';
        if (linked && /^(?:get|save)\b/i.test(clause)
            && !/^(?:get|save)\s+(?:me\s+)?(?:an?\s+|the\s+)?(?:summary|explanation|description|transcript|link|instructions?|command|code)\b/i.test(clause)
            && (/\b(?:video|clip|audio|file|offline|download)\b/i.test(clause)
                || /^(?:get|save)\s+(?:(?:me|this|that|it)\s+)*<link>/i.test(clause))) return 'retrieval';
        if ((linked || (mediaTarget && !/^transcribe\b/i.test(clause))) && !/\b(?:budget|cost|price)\b/i.test(clause) && /^(?:transcribe|convert|trim|compress|crop|cut|extract|merge|stitch)\b/i.test(clause)
            && (/\b(?:video|clip|audio|mp3|mp4|subtitle|srt|vtt)\b/i.test(clause)
                || /^transcribe\s+(?:(?:this|that)\s+)?<link>/i.test(clause))) return 'media-processing';
        if (linked && /^(?:generate|create|make)\b.{0,35}\b(?:srt|vtt|subtitles?|transcript)\b/i.test(clause)) return 'media-processing';
        if (linked && /^(?:take|capture|save)\b.{0,35}\b(?:screenshots?|screen ?captures?)\b/i.test(clause)) return 'browser';
        if (/^(?:open|visit|browse|go to|use)\b/i.test(clause)
            && (linked || /\b(?:browser|tab|website)\b/i.test(clause))
            && /(?:\b(?:and|to)\s+|,\s*)(?:click|fill|submit|log in|sign in|download|(?:take|capture)\s+a\s+screenshot)\b/i.test(clause)) return 'browser';
        if (/^(?:click|fill (?:in|out)|submit)\b/i.test(clause)
            && /\b(?:button|form|field|browser|tab|website)\b/i.test(clause)
            && (linked || target)) return 'browser';
        if (/^(?:run|execute)\s+(?:(?:all|the|this|my|our|attached|uploaded|unit|integration|regression|python|javascript|shell|terminal)\s+)*(?:code|script|command|tests?|test suite|notebook)\b|^(?:run|execute)\s+(?:(?:the |this )?following:|<code>|(?:python[23]?|node|npm|yarn|pnpm|bash|sh|pytest|git)\s)/i.test(clause)
            && !/\b(?:mentally|in your head|on paper|without (?:actually |really )?(?:running|executing))\b/i.test(clause)) return 'execution';
        if (/^(?:zip|unzip|unpack|archive|merge|combine|split|rotate|reorder|encrypt|decrypt)\b/i.test(clause)
            && /\b(?:files?|folders?|archives?|zip|pdfs?|documents?)\b/i.test(clause) && target) return 'file-processing';
        if (/^extract\b/i.test(clause) && /\b(?:archive|zip)\b/i.test(clause) && target) return 'file-processing';
        if (/^(?:rename|move|copy|delete|organize|organise)\b/i.test(clause)
            && /\b(?:files?|folders?|directories)\b/i.test(clause)
            && /\b(?:my|our|local|connected|uploaded|attached|these|those)\b/i.test(clause)) return 'execution';
        if (/^(?:deploy|redeploy)\b/i.test(clause)
            && /\b(?:site|website|app|application|server|service|project)\b/i.test(clause) && target) return 'deployment';
        if (/^(?:clone|fork)\b/i.test(clause) && (linked || /\b(?:repo|repository)\b/i.test(clause))) return 'execution';
        if (/^(?:edit|patch|refactor|migrate|fix)\b/i.test(clause)
            && /\b(?:in|inside|across)\s+(?:(?:my|our|the|this|connected|local)\s+)*(?:repo|repository|codebase|folder)\b/i.test(clause)) return 'execution';
    }
    return null;
}

// Resolve only a narrow media follow-up and only from the immediately preceding
// visible user turn. Assistant text, quotes, and old topics cannot supply tools
// or launch instructions. Carry the exact URL into the new Work prompt.
function resolveWorkCapabilityRequest(message, previousMessages = []) {
    const original = typeof message === 'string' ? message : '';
    const request = requestText(original);
    if (request.includes('<link>') || declinesWork(original)
        || !/^(?:please\s+)?(?:(?:can|could|would) you\s+)?(?:download|grab|save|transcribe|convert|trim)\s+(?:this|that|it|the (?:video|clip|audio|file))\b/i.test(request)) return original;
    const prior = [...previousMessages].reverse().find(item => item?.role === 'user' && !item.flags?.isHidden && !item.flags?.isDeleted);
    const source = typeof prior?.content === 'string' ? prior.content : '';
    if (/```|~~~|^\s*>|"|“/m.test(source)) return original;
    const urls = source.match(/https?:\/\/[^\s<>"'`]+/gi) || [];
    if (urls.length !== 1) return original;
    return `${original}\n\nSource from the previous user message: ${urls[0].replace(/[).,;!?]+$/, '')}`;
}

module.exports = { getWorkCapabilityGap, declinesWork, resolveWorkCapabilityRequest, workSuggestionPreference, workSuggestionsDeclined };
